import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { formatDateTime } from "../lib/dateFormat";
import { useAuth } from "../auth/AuthContext";
import { api, ApiError, type PlayerCommunication } from "../lib/api";
import PlayerTournamentBottomNav from "../components/PlayerTournamentBottomNav";
import TournamentSponsorSlots from "../components/TournamentSponsorSlots";
import { PlayerProfileScopeSwitcher } from "../components/PlayerFamilyProfileSwitcher";
import { drawResultsPath } from "../lib/drawResultsLink";
import { makeLatestOnly } from "../lib/latestOnly";
import { usePlayerProfileScope } from "../lib/usePlayerProfileScope";

const TYPE_LABELS: Record<string, string> = {
  general: "General",
  schedule_change: "Cambio de horario",
  group_change: "Cambio de grupo",
  result: "Resultado",
  standings: "Standings",
  rules: "Reglas",
  player_change: "Cambio de jugador",
};

/** Eduardo 2026-10-05: el aviso del sorteo se toca y abre los resultados de
 * ESE sorteo (su categoría). Los demás avisos, o uno cuyo sorteo ya no está
 * registrado, quedan como texto normal. */
function CommunicationTitle({ communication }: { communication: PlayerCommunication }) {
  const drawPath = drawResultsPath(communication.draw_results_url);
  if (!drawPath) {
    return <strong className="communications-feed__title">{communication.title}</strong>;
  }
  return (
    <Link className="communications-feed__link" to={drawPath}>
      <strong className="communications-feed__title">{communication.title}</strong>
      <span className="communications-feed__hint">Ver resultados del sorteo →</span>
    </Link>
  );
}

export default function PlayerCommunicationsPage() {
  const { id } = useParams<{ id?: string }>();
  const tournamentId = id ? Number(id) : NaN;
  const hasTournamentContext = Number.isFinite(tournamentId);
  const { token } = useAuth();
  const [communications, setCommunications] = useState<PlayerCommunication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Cuenta familiar: dentro de un torneo solo se cambia entre los hijos que
  // lo juegan; fuera, entre todos los perfiles de la cuenta.
  const scope = usePlayerProfileScope({ tournamentId: hasTournamentContext ? tournamentId : null });
  const scopeProfileId = scope.profileId;
  const latestLoad = useMemo(() => makeLatestOnly(), []);

  const load = useCallback(async () => {
    if (!token || !scope.ready) return;
    const attempt = latestLoad.begin();
    setLoading(true);
    setError(null);
    try {
      const response = hasTournamentContext
        ? await api.player.listTournamentCommunications(token, tournamentId, scopeProfileId)
        : await api.player.listCommunications(token, scopeProfileId);
      if (!latestLoad.isCurrent(attempt)) return;
      setCommunications(response.data.communications);
    } catch (err) {
      if (!latestLoad.isCurrent(attempt)) return;
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar los comunicados.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      if (latestLoad.isCurrent(attempt)) setLoading(false);
    }
  }, [hasTournamentContext, latestLoad, scope.ready, scopeProfileId, token, tournamentId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      {/* Secciones del torneo (Eduardo 2026-10-05): en computadora, pestañas
          aquí arriba; en celular, la barra flotante de abajo. */}
      {hasTournamentContext && <PlayerTournamentBottomNav tournamentId={tournamentId} />}
      <section className="card">
        <div className="card__header-row">
          <div>
            <h1 className="card__title">Comunicados</h1>
            <p className="card__subtitle">
              {hasTournamentContext
                ? "Avisos publicados para este torneo."
                : "Avisos publicados por los administradores de tus torneos."}
            </p>
          </div>
          <div className="form__actions">
            <Link className="btn btn--ghost" to="/player">
              Volver a mi inicio
            </Link>
          </div>
        </div>
        <PlayerProfileScopeSwitcher scope={scope} tournament={hasTournamentContext} />
        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
      </section>

      <section className="card">
        {loading ? (
          <p className="muted">Cargando comunicados…</p>
        ) : communications.length === 0 ? (
          <p className="muted">
            Sin comunicados por ahora. Cuando un admin publique un aviso lo
            verás aquí.
          </p>
        ) : (
          <ul className="communications-feed">
            {communications.map((c) => (
              <li key={c.id} className="communications-feed__item">
                <div className="communications-feed__meta">
                  <span className="status-pill">
                    {TYPE_LABELS[c.communication_type] ?? c.communication_type}
                  </span>
                  <span className="muted">
                    {c.tournament_name ?? `Torneo #${c.tournament_id}`}
                    {c.audience_type === "group" && c.group_name
                      ? ` · Grupo ${c.group_name}`
                      : ""}
                    {" · "}
                    {formatDateTime(c.published_at)}
                  </span>
                </div>
                <CommunicationTitle communication={c} />
                <p className="communications-feed__body">{c.body}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
      {hasTournamentContext && <TournamentSponsorSlots compact />}
    </>
  );
}
